import React, { useState } from 'react';
import { styled } from 'styled-components';
import { ClusteringLabGroup } from '../types';

interface ActionBarProps {
  selectedCount: number;
  groups: ClusteringLabGroup[];
  currentGroupId: string | null;
  onCreateGroup: (label: string) => void;
  canCreateGroup: boolean;
  onAddToGroup: (groupId: string) => void;
  onSetRepresentative: () => void;
  onMarkShareworthy: (isShareworthy: boolean) => void;
  onRemoveFromGroup: () => void;
  canRemove: boolean;
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #2d3748;
  color: white;
  border-top: 1px solid #4a5568;
  font-size: 13px;
`;

const Count = styled.span`
  font-weight: 600;
  margin-right: 8px;
`;

const Btn = styled.button`
  background: #4a5568;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: #718096;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NameInput = styled.input`
  background: #4a5568;
  color: white;
  border: 1px solid #718096;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  outline: none;
  width: 150px;

  &::placeholder {
    color: #a0aec0;
  }
`;

const ShareworthyBtn = styled.button`
  background: #276749;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: #48bb78;
  }
`;

const NotShareworthyBtn = styled.button`
  background: #9b2c2c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: #fc8181;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 4px;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  color: #2d3748;
  font-size: 13px;

  &:hover {
    background: #edf2f7;
  }
`;

const ActionBar: React.FC<ActionBarProps> = ({
  selectedCount,
  groups,
  currentGroupId,
  onCreateGroup,
  canCreateGroup,
  onAddToGroup,
  onSetRepresentative,
  onMarkShareworthy,
  onRemoveFromGroup,
  canRemove,
}) => {
  const [creating, setCreating] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  if (selectedCount === 0) return null;

  const childGroups = groups.filter((g) => g.parent_group_id === currentGroupId);

  const handleCreate = () => {
    if (groupName.trim()) {
      onCreateGroup(groupName.trim());
      setGroupName('');
      setCreating(false);
    }
  };

  return (
    <Bar>
      <Count>{selectedCount} selected</Count>

      {canCreateGroup &&
        (creating ? (
          <>
            <NameInput
              placeholder="Group name..."
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') setCreating(false);
              }}
              autoFocus
            />
            <Btn onClick={handleCreate} disabled={!groupName.trim()}>
              OK
            </Btn>
            <Btn onClick={() => setCreating(false)}>Cancel</Btn>
          </>
        ) : (
          <Btn onClick={() => setCreating(true)}>Create Group</Btn>
        ))}

      {childGroups.length > 0 && (
        <DropdownWrapper>
          <Btn onClick={() => setShowDropdown(!showDropdown)}>Add to ▾</Btn>
          {showDropdown && (
            <Dropdown>
              {childGroups.map((g) => (
                <DropdownItem
                  key={g.group_id}
                  onClick={() => {
                    onAddToGroup(g.group_id);
                    setShowDropdown(false);
                  }}
                >
                  {g.label}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
        </DropdownWrapper>
      )}

      {selectedCount === 1 && <Btn onClick={onSetRepresentative}>★ Set Rep</Btn>}

      <ShareworthyBtn onClick={() => onMarkShareworthy(true)}>Shareworthy</ShareworthyBtn>
      <NotShareworthyBtn onClick={() => onMarkShareworthy(false)}>Not Shareworthy</NotShareworthyBtn>

      {canRemove && <Btn onClick={onRemoveFromGroup}>Remove</Btn>}
    </Bar>
  );
};

export default ActionBar;
